import { apiFetch } from './api';

export interface ChatMessage {
  role: 'system' | 'user' | 'assistant';
  content: string;
}

export interface ChatCompletionParams {
  messages: ChatMessage[];
  model?: string;
  temperature?: number;
  max_tokens?: number;
}

export interface ChatCompletionResponse {
  choices: Array<{
    message: { content: string };
  }>;
}

export interface ImageGenerationParams {
  prompt: string;
  model?: string;
  n?: number;
  size?: string;
  quality?: string;
  style?: string;
}

export interface ImageGenerationResponse {
  data: Array<{ url: string }>;
}

/** Proxy a chat completion through the backend (keeps OpenAI key off the frontend). */
export async function proxyChatCompletion(
  params: ChatCompletionParams,
): Promise<ChatCompletionResponse> {
  const res = await apiFetch<{ success: boolean; data: ChatCompletionResponse }>(
    '/v1/ai/proxy/chat',
    { method: 'POST', body: params },
  );
  return (res as { data: ChatCompletionResponse }).data;
}

/** Proxy an image generation through the backend (keeps OpenAI key off the frontend). */
export async function proxyImageGeneration(
  params: ImageGenerationParams,
): Promise<ImageGenerationResponse> {
  const res = await apiFetch<{ success: boolean; data: ImageGenerationResponse }>(
    '/v1/ai/proxy/image',
    { method: 'POST', body: params },
  );
  return (res as { data: ImageGenerationResponse }).data;
}

// ============================================================================
// STRUCTURED AI ENDPOINTS (same as mobile app)
// ============================================================================

export interface AiIssueOption {
  issues: string[];
}

export interface AiAnalyzeIssueResult {
  issueTitle?: string;
  diagnosis: string;
  severity: string;
  requiresProfessional: boolean;
  possibleCauses: string[];
  quickChecks: string[];
  estimatedCost?: string;
  estimatedConsultMinutes?: number;
}

export interface AiDiyStep {
  stepNumber: number;
  title: string;
  description: string;
  instructions: string[];
  safety?: string;
  duration?: string;
  risk?: string;
  tools?: string[];
}

export interface AiDiyTroubleshootingResult {
  steps: AiDiyStep[];
  toolsNeeded: string[];
  estimatedTime: string;
  difficultyLevel: string;
  safetyWarnings: string[];
  whenToCallProfessional?: string;
}

export interface AiPart {
  name: string;
  qty: number;
  description: string;
  price: number;
}

export interface AiPartsResult {
  parts: AiPart[];
}

export interface AiMaintenancePart {
  name: string;
  qty: number;
  description: string;
}

export interface AiMaintenancePartsResult {
  parts: AiMaintenancePart[];
}

export interface AiKeyRisk {
  title: string;
  description: string;
  severity: string;
}

export interface AiMaintenanceTask {
  task: string;
  frequency: string;
  estimatedCost: string;
  priority: string;
  whyItMatters: string;
}

export interface AiAssetInsightsResult {
  keyRisks: AiKeyRisk[];
  maintenanceTasks: AiMaintenanceTask[];
}

/** Generate 6-8 common issues for an asset. */
export async function aiGenerateIssueOptions(payload: {
  assetName: string;
  assetBrand: string;
  assetModel?: string;
  assetType?: string;
}): Promise<AiIssueOption> {
  const res = await apiFetch<{ success: boolean; data: AiIssueOption }>(
    '/v1/ai/generate-issue-options',
    { method: 'POST', body: payload },
  );
  return (res as { data: AiIssueOption }).data;
}

/** Analyse a reported issue — returns diagnosis, severity, causes, quick checks. */
export async function aiAnalyzeIssue(payload: {
  assetName: string;
  assetBrand: string;
  assetModel?: string;
  issue: string;
  additionalDescription?: string;
}): Promise<AiAnalyzeIssueResult> {
  const res = await apiFetch<{ success: boolean; data: AiAnalyzeIssueResult }>(
    '/v1/ai/analyze-issue',
    { method: 'POST', body: payload },
  );
  return (res as { data: AiAnalyzeIssueResult }).data;
}

/** Generate a 3-step DIY troubleshooting guide for an issue. */
export async function aiGenerateDiyTroubleshooting(payload: {
  assetName: string;
  assetBrand: string;
  assetModel?: string;
  issue: string;
  additionalDescription?: string;
}): Promise<AiDiyTroubleshootingResult> {
  const res = await apiFetch<{ success: boolean; data: AiDiyTroubleshootingResult }>(
    '/v1/ai/generate-diy-troubleshooting',
    { method: 'POST', body: payload },
  );
  return (res as { data: AiDiyTroubleshootingResult }).data;
}

/** Generate parts needed to fix a reported issue. */
export async function aiGenerateParts(payload: {
  assetName: string;
  assetBrand: string;
  problem: string;
}): Promise<AiPartsResult> {
  const res = await apiFetch<{ success: boolean; data: AiPartsResult }>(
    '/v1/ai/generate-parts',
    { method: 'POST', body: payload },
  );
  return (res as { data: AiPartsResult }).data;
}

/** Generate parts needed for a scheduled maintenance task. */
export async function aiGenerateMaintenanceParts(payload: {
  taskName: string;
  assetName: string;
  assetLocation?: string;
  whyItMatters?: string;
  assetId?: string;
  taskDescription?: string;
}): Promise<AiMaintenancePartsResult> {
  const res = await apiFetch<{ success: boolean; data: AiMaintenancePartsResult }>(
    '/v1/ai/generate-maintenance-parts',
    { method: 'POST', body: payload },
  );
  return (res as { data: AiMaintenancePartsResult }).data;
}

/** Get key risks and maintenance schedule for an asset. */
export async function aiAssetInsights(payload: {
  assetName: string;
  brand?: string;
  model?: string;
  category?: string;
  subCategory?: string;
  ageYears?: number;
  healthScore?: number;
}): Promise<AiAssetInsightsResult> {
  const res = await apiFetch<{ success: boolean; data: AiAssetInsightsResult }>(
    '/v1/ai/asset-insights',
    { method: 'POST', body: payload },
  );
  return (res as { data: AiAssetInsightsResult }).data;
}
